import { create } from 'zustand';

import {
  BestOffering,
  PlansMetaData
} from 'src/services/responseTypes/plansMetaDataResponse';
import { TopSearchType } from 'src/services/responseTypes/topSearchResponse';
import { DEVICE_TYPES } from 'src/utils/constants/AppConstants';

import {
  AppActions,
  AppState,
  GamificationCampaignStatus,
  GamificationScreens,
  GamificationState
} from './interface';

export const getDefaultServerSideState = () => ({
  apiErrorResponse: {},
  initialLaunchData: null,
  usersPreferenceData: null,
  singlePlayData: null,
  translations: {},
  userWatchlist: {
    episodes: [],
    movies: [],
    videos: [],
    sports: [],
    plex: [],
    isWatchlistDataFetched: false
  },
  usersData: null,
  remoteConfig: [],
  comboOfferState: {
    isComboOfferEnabled: false,
    isComboOfferEnabledPending: null
  },
  usersLocationData: null,
  displayAdsData: null,
  isUserLoggedIn: false,
  plansMetaData: [],
  appConfig: {
    deviceType: DEVICE_TYPES.MOBILE,
    deviceWidth: 480,
    userType: '',
    pages: '',
    isRequestedByBot: false,
    isRequestedByPartner: false,
    isViUser: false,
    isWebPCompatible: false,
    isAvifCompatible: false,
    appHostname: '',
    actualDeviceType: DEVICE_TYPES.MOBILE
  },
  parentalData: { showParentalPinPageFlag: false },
  tvodRentals: {
    activeRentalPurchases: [],
    activeAssetIDs: [],
    expiredRentalPurchases: [],
    expiredAssetIDs: [],
    allRentalPurchases: [],
    uniqueRentalPurchases: [],
    tvodPlans: [],
    getPlansSuccess: false
  },
  subscriptionData: {
    currentActiveSubscriptions: [],
    maxPricePack: {}
  },
  popups: {
    showLanguageBanner: false,
    showLanguagePopup: false,
    isViTvod: false,
    isAvodPopupNudgeVisible: false,
    showReferralSubscriptionNudge: false,
    referralRedirectLink: '',
    referralSourceElement: ''
  },
  topSearch: [],
  searchLandingData: [],
  // seoMetaAssets: null,
  loadExternalScripts: false,
  companionAdState: {
    isAdPlayingOnPlayer: false,
    companionAdLoaded: false
  },
  tvodState: {
    isTvodContent: false,
    tvodPriceObj: null,
    planDetails: null,
    isTvodEvent: false,
    tvodOverlay: false,
    isTVODLogicResolved: false,
    isUserEntitledForTvod: false
  },
  gcpError: {
    code: '',
    message: ''
  },
  isGcpErrorShown: false,
  commerceData: [],
  commerceComponentData: [],
  commerceWishlistedProductsLength: 0,
  pageLoadPingUrl: '',
  bestOfferingPlan: null,
  upgradeData: null,
  gamificationState: {
    config: null,
    isQuizActive: false,
    gameId: null,
    settings: null,
    questions: [],
    currentScreen: GamificationScreens.Landing,
    campaignStatus: GamificationCampaignStatus.IDLE
  }
});

export const createAppStore = (state: any) => {
  return create<AppState & AppActions>()((set) => ({
    ...state,
    setInitialLaunchData: (data: any) =>
      set(() => ({ initialLaunchData: data })),
    setSearchLandingData: (data: any) =>
      set(() => ({ searchLandingData: data })),
    setErrorData: (data) => set(() => ({ apiErrorResponse: data })),
    setUsersData: (data) => set(() => ({ usersData: data })),
    setTranslations: (data) => {
      console.log('translations', data);
      set(() => ({ translations: data }));
    },
    setUserWatchlist: (data) => set(() => ({ userWatchlist: data })),
    setRemoteConfig: (data) => set(() => ({ remoteConfig: data })),
    setComboOfferState: (data) => set(() => ({ comboOfferState: data })),
    setUsersLocationData: (data) => set(() => ({ usersLocationData: data })),
    setAppConfig: (data) =>
      set((state) => ({ appConfig: { ...state.appConfig, ...data } })),
    setDisplayAdsData: (data) => set(() => ({ displayAdsData: data })),
    setPopups: (data) =>
      set((state) => ({ popups: { ...state.popups, ...data } })),
    setParentalData: (data) =>
      set((state) => ({ parentalData: { ...state.parentalData, ...data } })),
    setUserPackType: (packType: string) =>
      set((state) => ({ usersData: { ...state.usersData, packType } })),
    setTvodRentals: (data) =>
      set((state) => ({ tvodRentals: { ...state.tvodRentals, ...data } })),
    setSubscriptionData: (data) =>
      set((state) => ({
        subscriptionData: { ...state.subscriptionData, ...data }
      })),
    setUsersPreferenceData: (data) =>
      set((state) => ({
        usersPreferenceData: { ...state.usersPreferenceData, ...data }
      })),
    setIsUserLoggedIn: (data: boolean) => set(() => ({ isUserLoggedIn: data })),
    setTopSearch: (data: TopSearchType[]) => set(() => ({ topSearch: data })),
    setPlansMetaData: (data: PlansMetaData[]) =>
      set(() => ({ plansMetaData: data })),
    setExternalScripts: (data: boolean) =>
      set(() => ({ loadExternalScripts: data })),
    setCompanionAdState: (data) =>
      set((state) => ({
        companionAdState: { ...state.companionAdState, ...data }
      })),
    setTvodState: (data) =>
      set((state) => ({
        tvodState: { ...state.tvodState, ...data }
      })),
    setGcpError: (data) => set(() => ({ gcpError: data })),
    setIsGcpErrorShown: (data: boolean) =>
      set(() => ({ isGcpErrorShown: data })),
    setCommerceData: (data) =>
      set((state) => ({ commerceData: [data, ...state.commerceData] })),
    clearCommerceData: () => set(() => ({ commerceData: [] })),
    setCommerceComponentData: (data) =>
      set((state) => ({
        commerceComponentData: [...data, ...state.commerceComponentData]
      })),
    clearCommerceComponentData: () =>
      set(() => ({ commerceComponentData: [] })),
    setCommerceWishlistedProductsLength: (data: number) =>
      set(() => ({ commerceWishlistedProductsLength: data })),
    setPageLoadPingUrl: (data: string) =>
      set(() => ({ pageLoadPingUrl: data })),
    setBestOfferingPlan: (data: BestOffering) =>
      set(() => ({ bestOfferingPlan: data })),
    setUpgradeData: (data) => set(() => ({ upgradeData: data })),
    setGamificationState: (data: GamificationState) =>
      set((state) => ({
        gamificationState: { ...state.gamificationState, ...data }
      }))
  }));
};

if (process.env.NODE_ENV === 'development') {
  import('simple-zustand-devtools').then((Devtools) => {
    Devtools.mountStoreDevtool(
      'Store',
      createAppStore(getDefaultServerSideState())
    );
  });
}
